import { useEffect, useState, useCallback } from "react";
import { ShoppingBag, Wallet, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { colorHex } from "@/lib/colors";

export default function Shop() {
  const { user } = useAuth();
  const [merch, setMerch] = useState([]);
  const [wallet, setWallet] = useState(null);
  const isMember = user && user.role === "member";
  const [buyItem, setBuyItem] = useState(null);
  const [co, setCo] = useState({ name: "", email: "", phone: "", qty: 1, color: "", size: "" });
  const [busy, setBusy] = useState(false);

  const loadWallet = useCallback(() => {
    if (user) api.get("/wallet/me").then((r) => setWallet(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/merch").then((r) => setMerch(r.data)).catch(() => {});
    loadWallet();
  }, [loadWallet]);

  const openBuy = (m) => {
    const firstColor = (m.colors && m.colors.length) ? m.colors[0] : "";
    const firstSize = (m.sizes && m.sizes.length) ? (m.sizes.find((s) => s.in_stock)?.label || "") : "";
    setCo({ name: "", email: "", phone: "", qty: 1, color: firstColor, size: firstSize });
    setBuyItem(m);
  };

  const sizeUpcharge = (m, label) => {
    const s = (m?.sizes || []).find((x) => x.label === label);
    return s ? Number(s.upcharge || 0) : 0;
  };
  const unitPrice = buyItem ? Number(buyItem.price) + sizeUpcharge(buyItem, co.size) : 0;
  const total = unitPrice * co.qty;

  const validateVariant = () => {
    if (buyItem.colors && buyItem.colors.length && !co.color) { toast.error("Pick a color"); return false; }
    if (buyItem.sizes && buyItem.sizes.length && !co.size) { toast.error("Pick a size"); return false; }
    return true;
  };

  const redeem = async () => {
    if (!validateVariant()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/merch/redeem", { merch_id: buyItem.id, quantity: co.qty, color: co.color || null, size: co.size || null });
      toast.success(`Got it! ${buyItem.name} ordered with credit. $${data.remaining.toFixed(2)} left in your wallet.`);
      setWallet((w) => ({ ...w, available: data.remaining }));
      setBuyItem(null);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const payWithCard = async () => {
    if (!co.name.trim() || !co.email.trim() || !co.phone.trim()) { toast.error("Enter your name, email and phone"); return; }
    if (!validateVariant()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/merch/checkout", {
        merch_id: buyItem.id, quantity: co.qty,
        customer_name: co.name, customer_email: co.email, customer_phone: co.phone,
        color: co.color || null, size: co.size || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Checkout failed"); setBusy(false); }
  };

  const canAffordModal = isMember && (wallet?.available ?? 0) >= total;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <p className="eyebrow text-[#e0481f] text-sm mb-2">Rep The Movement</p>
      <h1 className="headline text-5xl sm:text-6xl text-[#f7f3ec] mb-3">The Shop</h1>
      <p className="text-[#a9a196] mb-6 max-w-2xl">Official I-65 Ryders gear. Members can spend their wallet credit right here.</p>

      {isMember && (
        <div className="card-warm rounded-2xl p-4 mb-8 inline-flex items-center gap-3" data-testid="shop-wallet">
          <Wallet className="text-[#e0481f]" size={22} />
          <span className="text-[#a9a196] text-sm">Your wallet credit:</span>
          <span className="headline text-2xl gold-text">${(wallet?.available ?? 0).toFixed(2)}</span>
        </div>
      )}

      {merch.length === 0 ? (
        <div className="card-warm rounded-3xl p-12 text-center">
          <Logo className="h-20 mx-auto mb-4 opacity-70" />
          <h2 className="headline text-3xl text-[#f7f3ec] mb-2">Merch Dropping Soon</h2>
          <p className="text-[#a9a196]">Official I-65 Ryders apparel is on the way. Check back shortly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
          {merch.map((m) => (
            <div key={m.id} className="card-warm rounded-2xl overflow-hidden group" data-testid={`merch-${m.id}`}>
              <div className="aspect-square overflow-hidden bg-[#1a1816] relative">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {m.featured && <span className="absolute top-3 left-3 bg-[#e0481f] text-black text-[10px] font-display uppercase tracking-wider px-2.5 py-1 rounded-full">Best Seller</span>}
              </div>
              <div className="p-4">
                <h3 className="headline text-xl text-[#f7f3ec]">{m.name}</h3>
                {m.description && <p className="text-[#a9a196] text-sm mb-1 line-clamp-2">{m.description}</p>}
                {m.colors && m.colors.length > 0 && (
                  <div className="flex items-center gap-1 mt-1 mb-1" data-testid={`merch-swatches-${m.id}`}>
                    {m.colors.slice(0, 8).map((cn) => (
                      <span key={cn} title={cn} className="w-3.5 h-3.5 rounded-full border border-[#3a332c] shadow-inner" style={{ backgroundColor: colorHex(cn) }} />
                    ))}
                    {m.colors.length > 8 && <span className="text-[#8f857a] text-xs ml-0.5">+{m.colors.length - 8}</span>}
                  </div>
                )}
                {m.sizes && m.sizes.length > 0 && (
                  <p className="text-[#8f857a] text-xs mb-1">{m.sizes.filter((s) => s.in_stock).length}/{m.sizes.length} sizes in stock</p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <span className="headline text-2xl gold-text">${m.price}</span>
                  <button onClick={() => openBuy(m)} className="btn-ember rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide inline-flex items-center gap-1.5" data-testid={`merch-buy-${m.id}`}>
                    <ShoppingBag size={15} /> Buy
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {buyItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="merch-checkout-modal">
          <div className="card-warm rounded-2xl p-6 w-full max-w-md relative max-h-[92vh] overflow-y-auto">
            <button onClick={() => setBuyItem(null)} className="absolute top-4 right-4 text-[#8f857a] hover:text-[#e0481f]" data-testid="merch-co-cancel"><X size={18} /></button>
            <h3 className="headline text-2xl text-[#f7f3ec] mb-1">Buy {buyItem.name}</h3>
            <p className="text-[#a9a196] text-sm mb-4">Pick your options and check out securely.</p>
            <div className="space-y-3">
              {buyItem.colors && buyItem.colors.length > 0 && (
                <div>
                  <label className="text-[#a9a196] text-xs uppercase tracking-wide">Color</label>
                  <select value={co.color} onChange={(e) => setCo({ ...co, color: e.target.value })} className="w-full mt-1 bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-co-color">
                    {buyItem.colors.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              {buyItem.sizes && buyItem.sizes.length > 0 && (
                <div>
                  <label className="text-[#a9a196] text-xs uppercase tracking-wide">Size</label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="merch-co-sizes">
                    {buyItem.sizes.map((s) => {
                      const active = co.size === s.label;
                      return (
                        <button key={s.label} type="button" disabled={!s.in_stock}
                          onClick={() => s.in_stock && setCo({ ...co, size: s.label })}
                          className={`px-3 py-2 rounded-xl text-sm font-display uppercase tracking-wide border transition-colors ${active ? "border-[#e0481f] bg-[#e0481f] text-black" : "border-[#3a332c] text-[#f7f3ec] hover:border-[#e0481f]"} ${!s.in_stock ? "opacity-30 line-through cursor-not-allowed" : ""}`}
                          data-testid={`merch-size-${s.label}`}>
                          {s.label}{s.upcharge > 0 ? ` +$${s.upcharge}` : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className="text-[#a9a196] text-sm">Qty</span>
                <input type="number" min={1} value={co.qty} onChange={(e) => setCo({ ...co, qty: Math.max(1, parseInt(e.target.value) || 1) })} className="w-20 bg-[#1a1816] border border-[#3a332c] rounded-xl px-3 py-2 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-co-qty" />
                <span className="ml-auto headline text-2xl gold-text" data-testid="merch-co-total">${total.toFixed(2)}</span>
              </div>
              <div className="border-t border-[#3a332c] pt-3 space-y-3">
                <input value={co.name} onChange={(e) => setCo({ ...co, name: e.target.value })} placeholder="Your name" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-co-name" />
                <input value={co.email} onChange={(e) => setCo({ ...co, email: e.target.value })} placeholder="Email (for your receipt)" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-co-email" />
                <input value={co.phone} onChange={(e) => setCo({ ...co, phone: e.target.value })} type="tel" autoComplete="tel" placeholder="Phone number" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-co-phone" />
                <button onClick={payWithCard} disabled={busy} className="btn-ember w-full rounded-full py-3 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="merch-co-pay">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />} Pay ${total.toFixed(2)}
                </button>
                {isMember && (
                  <button onClick={redeem} disabled={busy || !canAffordModal} className="btn-gold w-full rounded-full py-3 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="merch-co-credit">
                    <Wallet size={16} /> {canAffordModal ? `Use Wallet Credit ($${total.toFixed(2)})` : "Not enough credit"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
